import { initReactI18next, useTranslation as useTranslationBase } from 'react-i18next';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import moment from 'moment';
import { type TenantSettingsListTypeLowercase } from '@s20/slices/features/common.slice';
import en from './locales/en.json';
import es from './locales/es.json';
import 'moment/locale/es';

export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// Native display labels for the language picker. Per ROX-3412: show native names.
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
    en: 'English',
    es: 'Español',
};

const NAMESPACES = ['common', 'auth', 'calendar', 'timesheet', 'confirm', 'tenant', 'employee', 'nav', 'timeTracker'];

// Resolution order:
//   1. authenticated user's PreferredLanguage (applied by Layout once the
//      profile call returns)
//   2. navigator language / html lang attribute (this detector)
//   3. fallback to English
//
// localStorage is intentionally NOT in the detector chain — caching there
// caused stale values to bleed across users on a shared device, and the API
// preference is the source of truth for logged-in users anyway.
i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: { en, es },
        ns: NAMESPACES,
        defaultNS: 'common',
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
        nonExplicitSupportedLngs: true,
        load: 'languageOnly',
        interpolation: { escapeValue: false },
        detection: {
            order: ['navigator', 'htmlTag'],
            caches: [],
        },
        react: { useSuspense: true },
    });

const isSupported = (code: string): code is SupportedLanguage => (SUPPORTED_LANGUAGES as readonly string[]).includes(code);

// Keep moment locale aligned with i18n.
i18n.on('languageChanged', (lng) => {
    const [code] = (lng || DEFAULT_LANGUAGE).split('-');
    moment.locale(isSupported(code) ? code : DEFAULT_LANGUAGE);
});

const [initialCode] = (i18n.language || DEFAULT_LANGUAGE).split('-');
moment.locale(isSupported(initialCode) ? initialCode : DEFAULT_LANGUAGE);

// Locale-aware weekday display helpers. dayId follows JS Date.getDay():
// 0 = Sunday … 6 = Saturday — same as moment's internal index.
export const weekdayShort = (dayId: number): string => moment.weekdaysShort(dayId);
export const weekdayLetter = (dayId: number): string => {
    if (dayId === 4 && moment.locale().startsWith('en')) return 'R';
    return moment.weekdaysMin(dayId).charAt(0).toUpperCase();
};

// Tenant-context label for "Crew Lead". Union-enforcing tenants (WTC, other
// union operators) call this role "Driver"; everyone else calls them "Crew
// Lead". Pass the current tenantSettings; we read enforceContractUnionWork to
// pick the i18next context variant. This is the only language customization
// we honor — there is no general per-tenant override mechanism.
type CrewLeadVariant = 'singular' | 'plural' | 'upper';

export const crewLeadLabel = (
    tenantSettings: Pick<TenantSettingsListTypeLowercase, 'enforceContractUnionWork'>,
    variant: CrewLeadVariant = 'singular',
): string => {
    const context = tenantSettings?.enforceContractUnionWork ? 'union' : '';
    const keyByVariant: Record<CrewLeadVariant, string> = {
        singular: 'common:crewLead',
        plural: 'common:crewLeads',
        upper: 'common:crewLeadUpper',
    };
    return i18n.t(keyByVariant[variant], { context });
};

// react-i18next's useTranslation binds to the global i18n instance by default;
// our app configures this dedicated instance, so inject it here. Components
// should import useTranslation from '@s20/i18n' rather than 'react-i18next'.
export const useTranslation = (namespace?: string | string[]) =>
    useTranslationBase(namespace, { i18n });

export default i18n;
